/**
 * The item on screen during a call: the decision's options exactly as stored (verbatim labels,
 * stored order, spoken numbers), the suggested one marked with a small blue pill, and an
 * always-visible note. Keys 1–9 pick, `n` focuses the note, Enter sends the note (alone or with
 * the focused option). Nothing shrinks, moves or dims on focus or pick.
 */
import type { CallQueueItem } from '@viko/protocol'
import { Check, KeyRound, SkipForward } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { isTyping } from '../../lib/shortcut.js'

export function CallItemCard({
  item,
  position,
  onPick,
  onNote,
  onSkip,
  keys = true,
}: {
  item: CallQueueItem
  position?: string
  onPick: (n: number, note?: string) => void
  onNote: (text: string) => void
  onSkip?: () => void
  keys?: boolean
}) {
  const d = item.decision
  const suggested = d?.options.find((o) => o.suggested)?.n ?? 1
  const [focus, setFocus] = useState(suggested)
  const [picked, setPicked] = useState<number | undefined>()
  const [note, setNote] = useState('')
  const noteRef = useRef<HTMLInputElement>(null)
  const key = item.key

  // A new item starts fresh, with focus on the suggested option.
  const [seen, setSeen] = useState(key)
  if (seen !== key) {
    setSeen(key)
    setFocus(suggested)
    setPicked(undefined)
    setNote('')
  }

  const pick = (n: number) => {
    setPicked(n)
    onPick(n, note.trim() || undefined)
    setNote('')
  }

  useEffect(() => {
    if (!keys || !d) return
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return
      if (/^[1-9]$/.test(e.key)) {
        const n = Number(e.key)
        if (n <= d.options.length) {
          e.preventDefault()
          pick(n)
        }
      } else if (e.key === 'n') {
        e.preventDefault()
        noteRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  return (
    <section className="call-item call-breathe" aria-label={`Up next: ${item.title}`}>
      <div className="call-item__h">
        <span className="call-proj">{item.projectName}</span>
        {position ? <span className="call-meta num">{position}</span> : null}
      </div>
      <h2 className="call-item__q">{d?.question ?? item.title}</h2>
      {d?.secure ? (
        <p className="call-item__secure">
          <KeyRound size={16} aria-hidden="true" />
          Needs your passkey: say a number and Viko queues it for the batch.
        </p>
      ) : null}
      {d?.options.length ? (
        <div className="call-opts" role="radiogroup" aria-label="Options">
          {d.options.map((o) => (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={picked === o.n}
              tabIndex={focus === o.n ? 0 : -1}
              className={`call-opt${picked === o.n ? ' is-picked' : ''}`}
              data-option={o.n}
              onFocus={() => setFocus(o.n)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault()
                  const next = Math.min(
                    d.options.length,
                    Math.max(1, focus + (e.key === 'ArrowDown' ? 1 : -1)),
                  )
                  setFocus(next)
                  ;(
                    e.currentTarget.parentElement?.children[next - 1] as HTMLElement | undefined
                  )?.focus()
                }
              }}
              onClick={() => pick(o.n)}
            >
              <span className="call-opt__key" aria-hidden="true">
                {picked === o.n ? <Check size={14} /> : o.n}
              </span>
              <span className="call-opt__label">{o.label}</span>
              {o.suggested ? <span className="call-pill">Suggested</span> : <span />}
            </button>
          ))}
        </div>
      ) : null}
      <form
        className="call-note"
        onSubmit={(e) => {
          e.preventDefault()
          const t = note.trim()
          if (!t) return
          setNote('')
          onNote(t)
        }}
      >
        <label className="call-note__l" htmlFor={`call-note-${key}`}>
          Note <kbd className="call-kc">n</kbd>
        </label>
        <input
          id={`call-note-${key}`}
          ref={noteRef}
          className="call-note__in"
          value={note}
          // Fits one line at 390; "Note n" and the meta line say the rest.
          placeholder={d?.options.length ? 'Add a note…' : 'Your answer…'}
          onChange={(e) => setNote(e.target.value)}
        />
      </form>
      <div className="call-item__f">
        <span className="call-meta">
          {d?.options.length ? 'Say a number, or tap' : 'Answer out loud, or type a note'}
        </span>
        {onSkip ? (
          <button type="button" className="call-link-btn" onClick={onSkip}>
            <SkipForward size={16} aria-hidden="true" />
            Skip
          </button>
        ) : null}
      </div>
    </section>
  )
}
